import { datadogLogs } from '@datadog/browser-logs';
import { useAuth } from '@pesto/hubble-shared/providers/AuthProvider/AuthProvider';
import { createContext, type FC, type ReactNode, useContext, useState } from 'react';

import DuplicateSSNModal from '../components/modals/DuplicateSSNModal/DuplicateSSNModal';
import SessionExpiredModal from '../components/modals/SessionExpiredModal';

import { useSessionExpiredContext } from './SessionExpiredProvider';

interface ModalContextProps {
  openDuplicateSSNModal: (blocking: boolean, userId?: string) => void;
  openSessionExpiredModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

interface ModalState {
  isOpen: boolean;
  blocking: boolean;
}

const modalStateDefault: ModalState = {
  isOpen: false,
  blocking: false,
};

export const ModalProvider: FC<ModalProviderProps> = ({ children }) => {
  const [ssnModalState, setSsnModalState] = useState<ModalState>(modalStateDefault);

  const { signOut } = useAuth();
  const { isModalOpen, setSkipError, handleIsOpenModal, setErrorStatus } = useSessionExpiredContext();

  const handleSignOut = () => {
    signOut(true);
  };

  const openDuplicateSSNModal = (blocking: boolean, userId?: string) => {
    datadogLogs.logger.log('Duplicate SSN Modal shown', {
      userId: userId || 'Unknown User',
      modalState: {
        isOpen: true,
        blocking: blocking,
      },
      pageUrl: window.location.href,
      userAgent: window.navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
    setSsnModalState({
      isOpen: true,
      blocking: blocking,
    });
  };

  const closeDuplicateSSNModal = () =>
    setSsnModalState({
      isOpen: false,
      blocking: false,
    });
  const openSessionExpiredModal = () => handleIsOpenModal(true);
  const closeSessionExpiredModal = () => handleIsOpenModal(false);

  return (
    <ModalContext.Provider
      value={{
        openDuplicateSSNModal,
        openSessionExpiredModal,
      }}
    >
      {children}
      <SessionExpiredModal
        onClose={() => {
          setSkipError(true);
          setErrorStatus('None');
          closeSessionExpiredModal();
        }}
        isOpen={isModalOpen}
      />
      <DuplicateSSNModal
        isOpen={ssnModalState.isOpen}
        onClose={closeDuplicateSSNModal}
        onSignOut={handleSignOut}
        blocking={ssnModalState.blocking}
      />
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
